import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { IAuthUser } from '~/context/AuthContext';
import { LINE_OF_BUSINESS } from '~/core/constants/LineOfBusiness';

interface ClaimInfoItem {
  label: string;
  value?: string;
}

interface BreadcrumbItem {
  label: string;
  action?: () => void;
}

interface MainTopBarProps {
  user: IAuthUser | null;
  onLogout?: () => void;
  onToggleSidebar?: () => void;
  onToggleMenu?: () => void;
  claimId?: string;
  lineOfBusiness?: LINE_OF_BUSINESS;
  claimInfo?: Record<string, ClaimInfoItem>;
  breadcrumbItem?: BreadcrumbItem;
}

interface MainTopBarContextType {
  setMainTopBarProps: (props: Partial<MainTopBarProps>) => void;
  resetMainTopBar: () => void;
  props: MainTopBarProps;
}

const MainTopBarContext = createContext<MainTopBarContextType | null>(null);

interface MainTopBarProviderProps {
  children: ReactNode;
}

export const MainTopBarProvider: React.FC<MainTopBarProviderProps> = ({ children }) => {
  const [props, setProps] = useState<MainTopBarProps>({
    user: null,
    onLogout: () => null,
  });

  const setMainTopBarProps = useCallback((newProps: Partial<MainTopBarProps>) => {
    setProps((prev) => ({ ...prev, ...newProps }));
  }, []);

  const resetMainTopBar = useCallback(() => {
    setProps((prev) => ({
      ...prev,
      claimId: undefined,
      lineOfBusiness: undefined,
      claimInfo: undefined,
      breadcrumbItem: undefined,
    }));
  }, []);

  return (
    <MainTopBarContext.Provider value={{ props, setMainTopBarProps, resetMainTopBar }}>
      {children}
    </MainTopBarContext.Provider>
  );
};

export const useMainTopBar = () => {
  const context = useContext(MainTopBarContext);
  if (!context) {
    throw new Error('useMainTopBar must be used within MainTopBarProvider');
  }
  return context;
};

export type { MainTopBarProps, MainTopBarContextType };
